import React, { useState, useEffect, createContext, useContext, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigationType } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { LanguageProvider } from './context/LanguageContext';
import { ThemeProvider } from './context/ThemeContext';
import HomePage from './pages/HomePage';
import ArticlePage from './pages/ArticlePage';
import CategoryPage from './pages/CategoryPage';
import CallbackPage from './pages/CallbackPage';
import SessionTimeout from './components/SessionTimeout';
import AuthCallback from './components/AuthCallback';
import BugReportButton from './components/BugReportButton';
import PushPrompt from './components/PushPrompt';
import ChatbotWidget from './components/ChatbotWidget';
import BackToTop from './components/BackToTop';
import OnboardingSurvey from './components/OnboardingSurvey';
import AccessibilityWidget from './components/AccessibilityWidget';
import ComingSoonGate from './components/ComingSoonGate';
import useVisitorTracking from './hooks/useVisitorTracking';
import adminRoutes from './routes/AdminRoutes';

// Lazy-loaded pages (deferred — not needed at first paint)
const SearchPage = lazy(() => import('./pages/SearchPage'));
const AboutPage = lazy(() => import('./pages/static/AboutPage'));
const ContactPage = lazy(() => import('./pages/static/ContactPage'));
const ImprintPage = lazy(() => import('./pages/static/ImprintPage'));
const PrivacyPage = lazy(() => import('./pages/static/PrivacyPage'));
const CookiesPage = lazy(() => import('./pages/static/CookiesPage'));
const TermsPage = lazy(() => import('./pages/static/TermsPage'));
const CommentingRulesPage = lazy(() => import('./pages/static/CommentingRulesPage'));
const RepostingRulesPage = lazy(() => import('./pages/static/RepostingRulesPage'));
const SubmitNewsPage = lazy(() => import('./pages/static/SubmitNewsPage'));
const SubmitBugPage = lazy(() => import('./pages/static/SubmitBugPage'));
const ProfilePage = lazy(() => import('./pages/ProfilePage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const VerifyEmailPage = lazy(() => import('./pages/VerifyEmailPage'));
const DocumentationPage = lazy(() => import('./pages/DocumentationPage'));
const JobsPage = lazy(() => import('./pages/JobsPage'));
const ClassifiedsPage = lazy(() => import('./pages/ClassifiedsPage'));
const MarketingPage = lazy(() => import('./pages/MarketingPage'));
const AdvertisePage = lazy(() => import('./pages/AdvertisePage'));
const AdvertiserDashboard = lazy(() => import('./pages/AdvertiserDashboard'));
const SubscriptionPage = lazy(() => import('./pages/SubscriptionPage'));
const PaymentSuccessPage = lazy(() => import('./pages/PaymentSuccessPage'));
const AffiliatePage = lazy(() => import('./pages/AffiliatePage'));
const SponsoredMarketplacePage = lazy(() => import('./pages/SponsoredMarketplacePage'));
const AuthorPage = lazy(() => import('./pages/AuthorPage'));
const EventCalendarPage = lazy(() => import('./pages/EventCalendarPage'));
const UGCPortalPage = lazy(() => import('./pages/UGCPortalPage'));
const ForYouPage = lazy(() => import('./pages/ForYouPage'));
const RecipesPage = lazy(() => import('./pages/RecipesPage'));
const RecipeDetailPage = lazy(() => import('./pages/RecipeDetailPage'));
const LiveStreamPage = lazy(() => import('./pages/LiveStreamPage'));
const LoyaltyPage = lazy(() => import('./pages/LoyaltyPage'));
const DatingPage = lazy(() => import('./pages/DatingPage'));
const MajstorPage = lazy(() => import('./pages/MajstorPage'));
const DoktorPage = lazy(() => import('./pages/DoktorPage'));
const NajamPage = lazy(() => import('./pages/NajamPage'));
const PonudaPage = lazy(() => import('./pages/PonudaPage'));
const VibePosaoPage = lazy(() => import('./pages/VibePosaoPage'));
const VibePosaoJobDetail = lazy(() => import('./pages/VibePosaoJobDetail'));
const VibePosaoWishRoles = lazy(() => import('./pages/VibePosaoWishRoles'));
const VibePosaoSeekerDash = lazy(() => import('./pages/VibePosaoSeekerDash'));
const VibePosaoEmployerDash = lazy(() => import('./pages/VibePosaoEmployerDash'));

// GSC Verification Meta Tag — fetches once and injects into <head>
function GSCVerification() {
  useEffect(() => {
    const API = process.env.REACT_APP_BACKEND_URL;
    fetch(`${API}/api/gsc/verification-meta`).then(r => r.json()).then(data => {
      if (data.has_code && data.verification_code) {
        let meta = document.querySelector('meta[name="google-site-verification"]');
        if (!meta) {
          meta = document.createElement('meta');
          meta.name = 'google-site-verification';
          document.head.appendChild(meta);
        }
        meta.content = data.verification_code;
      }
    }).catch(() => {});
  }, []);
  return null;
}

// Suspense wrapper for lazy-loaded pages
const PageSuspense = ({ children }) => (
  <Suspense fallback={<div className="flex items-center justify-center h-screen bg-cream dark:bg-gray-900"><div className="w-8 h-8 border-3 border-sky-500 border-t-transparent rounded-full animate-spin" /></div>}>
    {children}
  </Suspense>
);

const AdminSuspense = ({ children }) => (
  <Suspense fallback={<div className="flex items-center justify-center h-screen"><div className="w-8 h-8 border-3 border-sky-500 border-t-transparent rounded-full animate-spin" /></div>}>
    {children}
  </Suspense>
);

// Context
export const ThemeContext = createContext({ theme: 'light', toggleTheme: () => {} });
export const useTheme = () => useContext(ThemeContext);

// Protected route component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth0();
  const localUser = JSON.parse(localStorage.getItem('ct_user') || 'null');
  const loggedIn = isAuthenticated || !!localUser;

  if (isLoading) return (
    <div className="min-h-screen flex items-center justify-center bg-cream">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-navy"></div>
    </div>
  );

  if (!loggedIn) return <Navigate to="/" replace />;

  return children;
};

const AdminRoute = ({ children }) => (
  <ProtectedRoute><AdminSuspense>{children}</AdminSuspense></ProtectedRoute>
);

function ScrollToTop() {
  const location = useLocation();
  const navType = useNavigationType();
  useEffect(() => {
    if (navType !== 'POP') {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }
  }, [location.pathname, navType]);
  return null;
}

function AppRoutes() {
  const location = useLocation();
  useVisitorTracking();
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      {/* Critical public routes (eager) */}
      <Route path="/" element={<HomePage />} />
      <Route path="/article/:slug" element={<ArticlePage />} />
      <Route path="/category/:slug" element={<CategoryPage />} />
      <Route path="/tag/:tag" element={<CategoryPage isTag={true} />} />
      <Route path="/callback" element={<CallbackPage />} />

      {/* Static pages (lazy) */}
      <Route path="/search" element={<PageSuspense><SearchPage /></PageSuspense>} />
      <Route path="/about" element={<PageSuspense><AboutPage /></PageSuspense>} />
      <Route path="/contact" element={<PageSuspense><ContactPage /></PageSuspense>} />
      <Route path="/imprint" element={<PageSuspense><ImprintPage /></PageSuspense>} />
      <Route path="/privacy" element={<PageSuspense><PrivacyPage /></PageSuspense>} />
      <Route path="/cookies" element={<PageSuspense><CookiesPage /></PageSuspense>} />
      <Route path="/terms" element={<PageSuspense><TermsPage /></PageSuspense>} />
      <Route path="/commenting-rules" element={<PageSuspense><CommentingRulesPage /></PageSuspense>} />
      <Route path="/reposting-rules" element={<PageSuspense><RepostingRulesPage /></PageSuspense>} />
      <Route path="/submit-news" element={<PageSuspense><SubmitNewsPage /></PageSuspense>} />
      <Route path="/submit-bug" element={<PageSuspense><SubmitBugPage /></PageSuspense>} />

      {/* Marketing & Subscription */}
      <Route path="/marketing" element={<PageSuspense><MarketingPage /></PageSuspense>} />
      <Route path="/advertise" element={<PageSuspense><AdvertisePage /></PageSuspense>} />
      <Route path="/advertise/success" element={<PageSuspense><AdvertisePage /></PageSuspense>} />
      <Route path="/advertiser-analytics" element={<PageSuspense><AdvertiserDashboard /></PageSuspense>} />
      <Route path="/subscription" element={<PageSuspense><SubscriptionPage /></PageSuspense>} />
      <Route path="/subscription/success" element={<PageSuspense><PaymentSuccessPage /></PageSuspense>} />
      <Route path="/affiliate-partnerships" element={<PageSuspense><AffiliatePage /></PageSuspense>} />

      {/* User Pages */}
      <Route path="/verify" element={<PageSuspense><VerifyEmailPage /></PageSuspense>} />
      <Route path="/docs" element={<PageSuspense><DocumentationPage /></PageSuspense>} />
      <Route path="/documentation" element={<PageSuspense><DocumentationPage /></PageSuspense>} />
      <Route path="/profile" element={<ProtectedRoute><PageSuspense><ProfilePage /></PageSuspense></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><PageSuspense><DashboardPage /></PageSuspense></ProtectedRoute>} />

      {/* Feature Pages */}
      <Route path="/jobs" element={<PageSuspense><JobsPage /></PageSuspense>} />
      <Route path="/classifieds" element={<PageSuspense><ClassifiedsPage /></PageSuspense>} />
      <Route path="/sponsored" element={<PageSuspense><SponsoredMarketplacePage /></PageSuspense>} />
      <Route path="/author/:name" element={<PageSuspense><AuthorPage /></PageSuspense>} />
      <Route path="/events" element={<PageSuspense><EventCalendarPage /></PageSuspense>} />
      <Route path="/submit-article" element={<PageSuspense><UGCPortalPage /></PageSuspense>} />
      <Route path="/for-you" element={<PageSuspense><ForYouPage /></PageSuspense>} />

      {/* Recipes */}
      <Route path="/recepti" element={<ComingSoonGate portalKey="recepti"><PageSuspense><RecipesPage /></PageSuspense></ComingSoonGate>} />
      <Route path="/recepti/:id" element={<ComingSoonGate portalKey="recepti"><PageSuspense><RecipeDetailPage /></PageSuspense></ComingSoonGate>} />

      {/* Live Streaming */}
      <Route path="/live/:id" element={<PageSuspense><LiveStreamPage /></PageSuspense>} />

      {/* Loyalty */}
      <Route path="/loyalty" element={<ProtectedRoute><PageSuspense><LoyaltyPage /></PageSuspense></ProtectedRoute>} />

      {/* Dating (Times Veze) */}
      <Route path="/veze" element={<ComingSoonGate portalKey="veze"><PageSuspense><DatingPage /></PageSuspense></ComingSoonGate>} />

      {/* Majstor (Times Majstor) */}
      <Route path="/majstor" element={<ComingSoonGate portalKey="majstor"><PageSuspense><MajstorPage /></PageSuspense></ComingSoonGate>} />

      {/* Doktor (Times Doktor) */}
      <Route path="/doktor" element={<ComingSoonGate portalKey="doktor"><PageSuspense><DoktorPage /></PageSuspense></ComingSoonGate>} />

      {/* VibePosao */}
      <Route path="/vibeposao" element={<ComingSoonGate portalKey="vibeposao"><PageSuspense><VibePosaoPage /></PageSuspense></ComingSoonGate>} />
      <Route path="/vibeposao/jobs/:id" element={<PageSuspense><VibePosaoJobDetail /></PageSuspense>} />
      <Route path="/vibeposao/wish-roles" element={<PageSuspense><VibePosaoWishRoles /></PageSuspense>} />
      <Route path="/vibeposao/seeker" element={<ProtectedRoute><PageSuspense><VibePosaoSeekerDash /></PageSuspense></ProtectedRoute>} />
      <Route path="/vibeposao/employer" element={<ProtectedRoute><PageSuspense><VibePosaoEmployerDash /></PageSuspense></ProtectedRoute>} />

      {/* Times Najam */}
      <Route path="/najam" element={<PageSuspense><NajamPage /></PageSuspense>} />

      {/* Ponuda Document Generator */}
      <Route path="/ponuda" element={<PageSuspense><PonudaPage /></PageSuspense>} />
      <Route path="/ponuda/:templateId" element={<PageSuspense><PonudaPage /></PageSuspense>} />

      {/* Admin Routes */}
      {adminRoutes(AdminRoute)}

      {/* Catch All */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  const [theme, setTheme] = useState(() => localStorage.getItem('ct-theme') || 'light');
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const [onboardingEmail, setOnboardingEmail] = useState('');

  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('ct-theme', theme);
  }, [theme]);

  // Show onboarding survey when user logs in for the first time after registration
  useEffect(() => {
    const checkOnboarding = () => {
      try {
        const pending = localStorage.getItem('ct_onboarding_pending');
        const done = localStorage.getItem('ct_onboarding_survey_done');
        const user = JSON.parse(localStorage.getItem('ct_user') || 'null');
        if (pending && !done && user) {
          setOnboardingEmail(user.email || pending);
          setOnboardingOpen(true);
          localStorage.removeItem('ct_onboarding_pending');
        }
      } catch {}
    };
    checkOnboarding();
    window.addEventListener('ct_auth_change', checkOnboarding);
    return () => window.removeEventListener('ct_auth_change', checkOnboarding);
  }, []);

  const toggleTheme = () => setTheme(t => t === 'light' ? 'dark' : 'light');

  return (
    <LanguageProvider>
    <ThemeProvider>
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      <div className="min-h-screen">
        <BrowserRouter>
          <ScrollToTop />
          <GSCVerification />
          <SessionTimeout />
          <AppRoutes />
          <BugReportButton />
          <PushPrompt />
          <ChatbotWidget />
          <AccessibilityWidget />
          <BackToTop />
          <OnboardingSurvey open={onboardingOpen} email={onboardingEmail} onClose={() => setOnboardingOpen(false)} />
          </BrowserRouter>
      </div>
    </ThemeContext.Provider>
    </ThemeProvider>
    </LanguageProvider>
  );
}
